import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient, readError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import { ShieldCheck, UserPlus, Hammer, AlertCircle, CheckCircle2, Search, BadgeCheck, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function InvitationClaim() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [siret, setSiret] = useState("");
  const [siretData, setSiretData] = useState(null);
  const [siretBusy, setSiretBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const verifySiret = async () => {
    const raw = siret.replace(/\s/g, "");
    if (!/^\d{9}(\d{5})?$/.test(raw)) {
      toast.error("SIRET (14 chiffres) ou SIREN (9 chiffres) attendu");
      return;
    }
    setSiretBusy(true);
    setSiretData(null);
    try {
      const { data } = await apiClient.get(`/siret/verify?siret=${raw}`);
      setSiretData(data);
      if (data.valid) toast.success(`Vérifié : ${data.denomination}`);
      else toast.error(data.message || "Entreprise non trouvée");
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setSiretBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/invitations/by-token/${token}`);
        setInv(data.invitation);
        if (data.invitation.status !== "pending") {
          setError(
            data.invitation.status === "claimed"
              ? "Cette invitation a déjà été utilisée."
              : "Cette invitation n'est plus valide.",
          );
        }
      } catch (e) {
        setError(readError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (pw.length < 6) {
      toast.error("Mot de passe trop court (6 caractères minimum)");
      return;
    }
    setBusy(true);
    try {
      const payload = { password: pw };
      if (inv?.role === "artisan" && siret) {
        payload.siret = siret.replace(/\s/g, "");
        if (siretData?.valid) payload.siret_data = siretData;
      }
      await apiClient.post(`/invitations/${token}/claim`, payload);
      await refresh();
      toast.success("Compte créé. Bienvenue sur Batisur !");
      nav("/dashboard");
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-10 mono text-sm">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-16">
        {error ? (
          <div className="border-2 border-[#0A0A0B] bg-white p-10 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-600" />
            <h1 className="display text-3xl mt-4">Invitation indisponible</h1>
            <p className="text-zinc-700 mt-3">{error}</p>
            <Link to="/register" className="inline-block mt-6">
              <button className="btn-anthracite">Créer un compte standard</button>
            </Link>
          </div>
        ) : (
          <div className="border-2 border-[#0A0A0B] bg-white p-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#FF4D00] flex items-center justify-center">
                {inv.role === "artisan" ? (
                  <Hammer className="w-6 h-6 text-white" strokeWidth={2.5} />
                ) : (
                  <UserPlus className="w-6 h-6 text-white" strokeWidth={2.5} />
                )}
              </div>
              <div>
                <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">// Invitation Batisur</div>
                <h1 className="display text-3xl mt-1">Bienvenue {inv.name.split(" ")[0]}</h1>
              </div>
            </div>

            <div className="mt-6 border-2 border-[#FF4D00] bg-orange-50 p-5">
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// De la part de</div>
              <div className="display text-xl mt-1">
                {inv.invited_by_name}{" "}
                <span className="mono text-xs uppercase text-zinc-500">· {inv.invited_by_role}</span>
              </div>
              {inv.message && (
                <p className="mt-3 text-sm text-zinc-700 italic border-l-2 border-[#FF4D00] pl-3">
                  "{inv.message}"
                </p>
              )}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-0 border-2 border-[#0A0A0B]">
              <Field label="Email" value={inv.email} mono />
              <Field label="Rôle" value={inv.role === "artisan" ? "Artisan" : "Demandeur"} />
              {inv.phone && <Field label="Téléphone" value={inv.phone} mono />}
              {inv.company && <Field label="Société" value={inv.company} />}
              {inv.city && <Field label="Ville" value={inv.city} />}
              {inv.specialties?.length > 0 && (
                <Field label="Spécialités" value={inv.specialties.join(" · ")} />
              )}
            </div>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invitation-claim-form">
              {inv.role === "artisan" && (
                <div className="border-2 border-[#0A0A0B] p-4">
                  <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-[#FF4D00]">
                    <BadgeCheck className="w-3.5 h-3.5" /> // Vérification SIRET (optionnelle mais recommandée)
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    Votre numéro SIRET sera vérifié officiellement via Insee/Sirene. Vous obtiendrez un badge "Vérifié" sur votre profil.
                  </div>
                  <div className="flex gap-2 mt-3">
                    <input
                      value={siret}
                      onChange={(e) => { setSiret(e.target.value); setSiretData(null); }}
                      placeholder="14 chiffres (SIRET) ou 9 chiffres (SIREN)"
                      className="flex-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white mono text-sm"
                      data-testid="claim-siret"
                    />
                    <button
                      type="button"
                      onClick={verifySiret}
                      disabled={siretBusy || !siret.trim()}
                      className="btn-anthracite text-xs flex items-center gap-2 disabled:opacity-60"
                      data-testid="claim-siret-verify"
                    >
                      {siretBusy ? "..." : <><Search className="w-3.5 h-3.5" /> Vérifier</>}
                    </button>
                  </div>
                  {siretData?.valid && (
                    <div className="mt-3 border-2 border-emerald-600 bg-emerald-50 p-3" data-testid="siret-result-valid">
                      <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        {siretData.denomination}
                        {siretData.active ? (
                          <span className="badge-pill bg-emerald-600 text-white border-emerald-700">Actif</span>
                        ) : (
                          <span className="badge-pill bg-red-600 text-white border-red-700">Cessé</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-emerald-900">
                        {siretData.naf_code && <div>NAF : <strong>{siretData.naf_code}</strong></div>}
                        {siretData.creation_date && <div>Création : <strong>{siretData.creation_date}</strong></div>}
                        {siretData.city && <div>Siège : <strong>{siretData.city}</strong></div>}
                        {siretData.legal_form && <div>Forme : <strong>{siretData.legal_form}</strong></div>}
                      </div>
                    </div>
                  )}
                  {siretData && !siretData.valid && (
                    <div className="mt-3 border-2 border-red-300 bg-red-50 p-3 flex items-center gap-2 text-sm text-red-800" data-testid="siret-result-invalid">
                      <XCircle className="w-4 h-4" /> {siretData.message}
                    </div>
                  )}
                </div>
              )}

              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
                // Définissez votre mot de passe
              </div>
              <div>
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Mot de passe</label>
                <input
                  type="password"
                  required
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                  placeholder="6 caractères minimum"
                  data-testid="claim-password"
                />
              </div>
              <div>
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Confirmer</label>
                <input
                  type="password"
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                  data-testid="claim-password-confirm"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="btn-batisur w-full flex items-center justify-center gap-2"
                data-testid="claim-submit"
              >
                <CheckCircle2 className="w-4 h-4" />
                {busy ? "Création…" : "Activer mon compte"}
              </button>
              <div className="flex items-center gap-2 mt-3 text-xs text-zinc-500">
                <ShieldCheck className="w-3.5 h-3.5 text-[#FF4D00]" /> Paiement sécurisé Stripe — vos
                données restent confidentielles
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="p-3 border-r border-b border-zinc-200 last-of-type:border-r-0">
      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-sm mt-1 ${mono ? "mono" : ""} break-all`}>{value}</div>
    </div>
  );
}
